import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { DndContext, DragEndEvent, DragOverEvent, PointerSensor, pointerWithin, useDroppable, useSensor, useSensors } from '@dnd-kit/core'
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Button } from './Button'
import { Modal, ModalHeader, ModalBody, ModalFooter } from './Modal'
// The DS tab strip, not a second copy of one: the underline tablist already exists there, and the ds → ds2
// import is precedented in this folder (see TablePagination).
import { Tabs } from '@/components/ds2'

// ==========================================
// Public API (unchanged from the drawer version — only the visual/interaction
// layer changed to a modal with a 2-pane Visible/Hidden editor that mirrors the
// scheduler DayView's `ColumnsOrders`. See plan `calm-spinning-turing.md`
// Phase 2).
// ==========================================

export interface SettingsColumn<T extends string = string> {
    id: T
    label: string
    locked?: boolean
}

interface TableSettingsProps<T extends string = string> {
    open: boolean
    onClose: () => void
    title: string
    columns: SettingsColumn<T>[]
    visibleColumns: Record<T, boolean>
    onColumnChange: (column: T) => void
    columnOrder?: T[]
    onColumnOrderChange?: (order: T[]) => void
    /**
     * Minimum number of columns that must stay visible. When the user tries
     * to drop below this, hide-direction interactions are disabled and a
     * tooltip explains why. Defaults to 1.
     */
    minVisibleColumns?: number
    /** Called when the user hits "Reset default". If unset the button is hidden. */
    onReset?: () => void
    /** Optional save action for settings that persist changes instead of applying them immediately. */
    onSave?: () => void
    saveLabel?: string
    saving?: boolean
    error?: string | null
    /**
     * Settings beyond the columns, each as its own tab beside them.
     *
     * Omitted, this dialog is exactly what it was: one body, no tab strip. A table whose settings grew past
     * "which columns" says so here rather than opening a second dialog of its own — the operator looks for
     * table settings in one place, and the column panes are the thing they already know lives there.
     *
     * The columns tab carries its own reserved id, so no value here can collide with it.
     */
    tabs?: { id: string; label: string; content: ReactNode }[]
    /** What the columns tab is called. Defaults to "Columns". */
    columnsTabLabel?: string
}

type PaneId = 'visible' | 'hidden'

// ==========================================
// Icons
// ==========================================

const DragHandle = () => (
    <svg className="h-4 w-4 shrink-0 text-slate-400 dark:text-slate-500" viewBox="0 0 16 16" fill="currentColor">
        <circle cx="5" cy="2.5" r="1.5" />
        <circle cx="11" cy="2.5" r="1.5" />
        <circle cx="5" cy="8" r="1.5" />
        <circle cx="11" cy="8" r="1.5" />
        <circle cx="5" cy="13.5" r="1.5" />
        <circle cx="11" cy="13.5" r="1.5" />
    </svg>
)

const ArrowRight = () => (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
    </svg>
)

const ArrowLeft = () => (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" />
    </svg>
)

const LockIcon = () => (
    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
        />
    </svg>
)

// ==========================================
// Sortable row inside a pane
//
// Uses `useSortable` (rather than `useDraggable`) so each row registers BOTH a
// drag source and a drop target with the same id. Without the drop-target
// side, dragging between rows inside the same pane would have no collision
// surface to land on — that was the reorder bug the user reported
// ("hamma joyda settings bosilsa columnlarni ozgartirib bolmayabdi joyini").
// ==========================================

interface ColumnRowProps<T extends string> {
    col: SettingsColumn<T>
    pane: PaneId
    canMoveOut: boolean
    onMove: () => void
}

const ColumnRow = <T extends string>({ col, pane, canMoveOut, onMove }: ColumnRowProps<T>) => {
    const sortable = useSortable({
        id: `${pane}:${col.id}`,
        data: { pane, id: col.id },
        disabled: col.locked,
    })

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(sortable.transform),
        transition: sortable.transition ?? 'transform 180ms cubic-bezier(0.2, 0, 0, 1)',
        opacity: sortable.isDragging ? 0.4 : 1,
    }

    return (
        <div
            ref={sortable.setNodeRef}
            style={style}
            className={`flex items-center gap-2 rounded-lg border px-2.5 py-2 transition-colors ${
                col.locked
                    ? 'border-slate-100 bg-slate-50/60 dark:border-slate-800 dark:bg-slate-800/40'
                    : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:hover:border-slate-600 dark:hover:bg-slate-800'
            }`}
        >
            <span
                className={`shrink-0 rounded-md px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
                    pane === 'visible'
                        ? 'bg-blue-500/15 text-blue-700 dark:text-blue-400'
                        : 'bg-slate-200/70 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                }`}
            >
                {pane === 'visible' ? 'Visible' : 'Hidden'}
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-slate-900 dark:text-slate-100">{col.label}</span>
            {col.locked ? (
                <span className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400 dark:text-slate-500">
                    <LockIcon /> Locked
                </span>
            ) : (
                <>
                    <button
                        type="button"
                        onClick={onMove}
                        disabled={!canMoveOut}
                        title={canMoveOut ? (pane === 'visible' ? 'Hide column' : 'Show column') : 'Keep at least one column visible'}
                        className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-40 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-100"
                    >
                        {pane === 'visible' ? <ArrowRight /> : <ArrowLeft />}
                    </button>
                    <span {...sortable.listeners} {...sortable.attributes} className="cursor-grab text-slate-400 active:cursor-grabbing dark:text-slate-500">
                        <DragHandle />
                    </span>
                </>
            )}
        </div>
    )
}

// ==========================================
// Pane (drop target for the "drop into empty area" cross-pane move)
// ==========================================

interface PaneProps {
    id: PaneId
    title: string
    count: number
    children: React.ReactNode
    emptyMessage: string
    highlight: boolean
}

const Pane = ({ id, title, count, children, emptyMessage, highlight }: PaneProps) => {
    const { setNodeRef } = useDroppable({ id: `pane:${id}` })
    return (
        <div className="flex min-w-0 flex-1 flex-col">
            <div className="mb-2 flex items-baseline justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
                    {title} <span className="ml-1 text-slate-400 dark:text-slate-500">({count})</span>
                </span>
            </div>
            <div
                ref={setNodeRef}
                className={`flex min-h-[280px] flex-1 flex-col gap-1.5 rounded-lg border border-dashed p-2 transition-colors ${
                    highlight
                        ? 'border-blue-400 bg-blue-50/50 dark:border-blue-500/60 dark:bg-blue-500/10'
                        : 'border-slate-200 bg-slate-50/40 dark:border-slate-700 dark:bg-slate-800/30'
                }`}
            >
                {count === 0 ? (
                    <div className="flex flex-1 items-center justify-center text-xs text-slate-400 dark:text-slate-500">{emptyMessage}</div>
                ) : (
                    children
                )}
            </div>
        </div>
    )
}

// ==========================================
// TableSettings Component
// ==========================================

export const TableSettings = <T extends string = string>({
    open,
    onClose,
    title,
    columns,
    visibleColumns,
    onColumnChange,
    columnOrder,
    onColumnOrderChange,
    minVisibleColumns = 1,
    onReset,
    onSave,
    saveLabel = 'Done',
    saving = false,
    error,
    tabs,
    columnsTabLabel = 'Columns',
}: TableSettingsProps<T>) => {
    // Not 'columns': the id shares a namespace with whatever a caller passes, and a caller's own
    // `{ id: 'columns' }` would have given two buttons both reading as selected over a body that always
    // showed the panes.
    const COLUMNS_TAB = '__table-settings-columns__'
    const [activeTab, setActiveTab] = useState<string>(COLUMNS_TAB)
    // Defensive, and cheap: an id colliding with the columns tab would be a button selecting a body it never renders.
    const extraTabs = useMemo(() => (tabs ?? []).filter((tab) => tab.id !== COLUMNS_TAB), [tabs])
    // Back to the columns whenever the dialog is re-opened: the tab is a place in a dialog, not a preference,
    // and landing on whatever was last looked at hides the panes most openings are after.
    useEffect(() => {
        if (open) setActiveTab(COLUMNS_TAB)
    }, [open])
    // Local order reflecting any in-progress drag. Seeded from the caller's
    // `columnOrder` (visible-only) plus any remaining hidden columns appended.
    // We keep a local copy so the modal can show the drop target instantly
    // without waiting for the parent re-render.
    const seedOrder = useMemo<T[]>(() => {
        const safeColumns = columns ?? []
        const known = new Set(columnOrder ?? [])
        const visibleFirst = columnOrder ? [...columnOrder] : []
        const hiddenRest = safeColumns.filter((c) => !known.has(c.id)).map((c) => c.id)
        return [...visibleFirst, ...hiddenRest]
    }, [columns, columnOrder])

    const [localOrder, setLocalOrder] = useState<T[]>(seedOrder)
    const [overPane, setOverPane] = useState<PaneId | null>(null)

    useEffect(() => {
        if (!open) setLocalOrder(seedOrder)
        // Re-sync localOrder whenever the modal is re-opened with fresh props.
    }, [open, seedOrder])

    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }))

    const columnMap = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns])

    const visibleIds = useMemo(() => (localOrder ?? []).filter((id) => visibleColumns?.[id]), [localOrder, visibleColumns])
    const hiddenIds = useMemo(() => (localOrder ?? []).filter((id) => !visibleColumns?.[id]), [localOrder, visibleColumns])

    const visibleUnlockedCount = useMemo(() => visibleIds.filter((id) => !columnMap.get(id)?.locked).length, [visibleIds, columnMap])
    const canHideMore = visibleUnlockedCount > minVisibleColumns

    const moveToPane = useCallback(
        (id: T, targetPane: PaneId) => {
            const col = columnMap.get(id)
            if (!col || col.locked) return
            const isVisible = visibleColumns[id]
            if (targetPane === 'visible' && !isVisible) {
                onColumnChange(id)
            } else if (targetPane === 'hidden' && isVisible && canHideMore) {
                onColumnChange(id)
            }
        },
        [columnMap, visibleColumns, onColumnChange, canHideMore]
    )

    const handleDragStart = useCallback(() => {
        setOverPane(null)
    }, [])

    const handleDragOver = useCallback((e: DragOverEvent) => {
        const overId = e.over?.id ? String(e.over.id) : null
        if (!overId) {
            setOverPane(null)
            return
        }
        if (overId.startsWith('pane:')) {
            setOverPane(overId.slice(5) as PaneId)
        } else if (overId.includes(':')) {
            setOverPane(overId.split(':')[0] as PaneId)
        }
    }, [])

    const handleDragEnd = useCallback(
        (e: DragEndEvent) => {
            setOverPane(null)
            const activeRaw = String(e.active.id)
            const overRaw = e.over?.id ? String(e.over.id) : null
            if (!overRaw) return

            const [, activeColId] = activeRaw.split(':') as [PaneId, T]
            const activePane = activeRaw.split(':')[0] as PaneId
            if (!activeColId) return

            // 1) Dropped on the other pane's container (empty area) → just move across panes.
            if (overRaw.startsWith('pane:')) {
                const targetPane = overRaw.slice(5) as PaneId
                if (targetPane !== activePane) moveToPane(activeColId, targetPane)
                return
            }

            // 2) Dropped on another row — may be same pane (reorder) or other pane (move + place).
            const [overPaneId, overColId] = overRaw.split(':') as [PaneId, T]
            if (!overColId) return

            if (overPaneId !== activePane) {
                // Cross-pane move first (the column toggles visibility via the caller).
                moveToPane(activeColId, overPaneId)
            }

            // Reorder within the combined localOrder list: move activeColId next to overColId.
            const next = [...localOrder]
            const from = next.indexOf(activeColId)
            const to = next.indexOf(overColId)
            if (from !== -1 && to !== -1 && from !== to) {
                const [moved] = next.splice(from, 1)
                const insertAt = next.indexOf(overColId)
                next.splice(insertAt, 0, moved)
                setLocalOrder(next)
                // Only emit the visible-portion to the caller. Hidden columns' order is kept in
                // localOrder but the parent (ProfileTable) uses only the visible slice in its
                // `settingsColumnOrder` — so the callback signature stays consistent.
                const visibleNext = next.filter((id) => visibleColumns[id])
                onColumnOrderChange?.(visibleNext)
            }
        },
        [localOrder, moveToPane, onColumnOrderChange, visibleColumns]
    )

    // Stable item lists for each SortableContext. Prefixing with the pane id
    // guarantees unique sortable ids across both panes.
    const visibleSortableItems = useMemo(() => visibleIds.map((id) => `visible:${id}`), [visibleIds])
    const hiddenSortableItems = useMemo(() => hiddenIds.map((id) => `hidden:${id}`), [hiddenIds])

    return (
        <Modal open={open} onClose={onClose} size="lg">
            <ModalHeader onClose={onClose}>{title}</ModalHeader>
            <ModalBody className="flex flex-col gap-4">
                {extraTabs.length > 0 && (
                    <Tabs
                        variant="secondary"
                        items={[{ key: COLUMNS_TAB, label: columnsTabLabel }, ...extraTabs.map(({ id, label }) => ({ key: id, label }))]}
                        selectedKey={activeTab}
                        onSelectionChange={setActiveTab}
                    />
                )}
                {activeTab !== COLUMNS_TAB ? (
                    extraTabs.find((tab) => tab.id === activeTab)?.content
                ) : (
                    <DndContext
                        sensors={sensors}
                        collisionDetection={pointerWithin}
                        onDragStart={handleDragStart}
                        onDragOver={handleDragOver}
                        onDragEnd={handleDragEnd}
                    >
                        <div className="flex gap-3">
                            <Pane
                                id="visible"
                                title="Visible Columns"
                                count={visibleIds.length}
                                emptyMessage="Drag columns here to show them."
                                highlight={overPane === 'visible'}
                            >
                                <SortableContext items={visibleSortableItems} strategy={verticalListSortingStrategy}>
                                    {visibleIds.map((id) => {
                                        const col = columnMap.get(id)
                                        if (!col) return null
                                        return (
                                            <ColumnRow
                                                key={id}
                                                col={col}
                                                pane="visible"
                                                canMoveOut={canHideMore && !col.locked}
                                                onMove={() => moveToPane(id, 'hidden')}
                                            />
                                        )
                                    })}
                                </SortableContext>
                            </Pane>
                            <Pane
                                id="hidden"
                                title="Hidden Columns"
                                count={hiddenIds.length}
                                emptyMessage="No hidden columns."
                                highlight={overPane === 'hidden'}
                            >
                                <SortableContext items={hiddenSortableItems} strategy={verticalListSortingStrategy}>
                                    {hiddenIds.map((id) => {
                                        const col = columnMap.get(id)
                                        if (!col) return null
                                        return <ColumnRow key={id} col={col} pane="hidden" canMoveOut={!col.locked} onMove={() => moveToPane(id, 'visible')} />
                                    })}
                                </SortableContext>
                            </Pane>
                        </div>
                    </DndContext>
                )}
            </ModalBody>
            <ModalFooter>
                {error && (
                    <p role="alert" className="mr-auto text-sm text-red-600 dark:text-red-400">
                        {error}
                    </p>
                )}
                {onReset && activeTab === COLUMNS_TAB && (
                    <Button variant="ghost" size="sm" onClick={onReset}>
                        Reset default
                    </Button>
                )}
                <Button variant="primary" size="sm" onClick={onSave ?? onClose} disabled={saving}>
                    {saving ? 'Saving…' : saveLabel}
                </Button>
            </ModalFooter>
        </Modal>
    )
}
